import ScheduledDraftsAPI from '../../api/scheduledDrafts';
import Vue from 'vue';

const state = {
  records: [], // Store as array to maintain backend order
  recordsById: {}, // Keep object for quick ID lookups
  uiFlags: {
    isFetching: false,
    isUpdating: false,
    isApproving: false,
    isRegenerating: false,
    isDeleting: false,
  },
  meta: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    perPage: 15,
  },
  selectedDraftId: null,
  statusFilter: 'all',
  labelFilter: 'all',
  currentConversationId: null,
};

export const getters = {
  getDrafts: _state => _state.records, // Return array directly (maintains order)
  getDraft: _state => id => _state.recordsById[id], // Lookup by ID from object
  getSelectedDraft: _state =>
    _state.selectedDraftId ? _state.recordsById[_state.selectedDraftId] : null,
  getUIFlags: _state => _state.uiFlags,
  getMeta: _state => _state.meta,
  getStatusFilter: _state => _state.statusFilter,
  getLabelFilter: _state => _state.labelFilter,
  getCurrentConversationId: _state => _state.currentConversationId,
};

export const actions = {
  async fetchDrafts({ commit }, { conversationId, page = 1, status = 'all', label = 'all' }) {
    commit('SET_UI_FLAG', { flag: 'isFetching', value: true });
    commit('SET_CURRENT_CONVERSATION_ID', conversationId);
    commit('SET_STATUS_FILTER', status);
    commit('SET_LABEL_FILTER', label);

    try {
      const response = await ScheduledDraftsAPI.getAll(conversationId, {
        page,
        status,
        label,
      });
      commit('SET_DRAFTS', {
        data: response.data.data,
        meta: response.data.meta,
      });
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { flag: 'isFetching', value: false });
    }
  },

  async fetchDraft({ commit }, { conversationId, draftId }) {
    commit('SET_UI_FLAG', { flag: 'isFetching', value: true });
    try {
      const response = await ScheduledDraftsAPI.get(conversationId, draftId);
      commit('SET_DRAFT', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { flag: 'isFetching', value: false });
    }
  },

  async updateDraft({ commit }, { conversationId, draftId, data }) {
    commit('SET_UI_FLAG', { flag: 'isUpdating', value: true });
    try {
      const response = await ScheduledDraftsAPI.update(
        conversationId,
        draftId,
        data
      );
      commit('UPDATE_DRAFT', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { flag: 'isUpdating', value: false });
    }
  },

  async approveDraft({ commit }, { conversationId, draftId }) {
    commit('SET_UI_FLAG', { flag: 'isApproving', value: true });
    try {
      const response = await ScheduledDraftsAPI.approve(conversationId, draftId);
      commit('UPDATE_DRAFT', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { flag: 'isApproving', value: false });
    }
  },

  async regenerateDraft({ commit }, { conversationId, draftId, prompt }) {
    commit('SET_UI_FLAG', { flag: 'isRegenerating', value: true });
    try {
      const response = await ScheduledDraftsAPI.regenerate(
        conversationId,
        draftId,
        prompt
      );
      commit('UPDATE_DRAFT', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { flag: 'isRegenerating', value: false });
    }
  },

  async deleteDraft({ commit, dispatch }, { conversationId, draftId }) {
    commit('SET_UI_FLAG', { flag: 'isDeleting', value: true });
    try {
      await ScheduledDraftsAPI.delete(conversationId, draftId);
      // Backend changes status to 'cancelled' but doesn't delete the record
      // Refetch the draft to get updated status
      const response = await ScheduledDraftsAPI.get(conversationId, draftId);
      commit('UPDATE_DRAFT', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { flag: 'isDeleting', value: false });
    }
  },

  setStatusFilter({ commit }, status) {
    commit('SET_STATUS_FILTER', status);
  },

  setLabelFilter({ commit }, label) {
    commit('SET_LABEL_FILTER', label);
  },

  setSelectedDraft({ commit }, draftId) {
    commit('SET_SELECTED_DRAFT', draftId);
  },

  clearDrafts({ commit }) {
    commit('CLEAR_DRAFTS');
  },
};

export const mutations = {
  SET_DRAFTS(_state, { data, meta }) {
    // Store as array to maintain backend order
    _state.records = data;
    
    // Also store in object for quick ID lookups
    const recordsById = {};
    data.forEach(draft => {
      recordsById[draft.id] = draft;
    });
    _state.recordsById = recordsById;
    
    _state.meta = {
      currentPage: meta.current_page,
      totalPages: meta.total_pages,
      totalCount: meta.total_count,
      perPage: meta.per_page,
    };
  },

  SET_DRAFT(_state, draft) {
    // Add to recordsById
    Vue.set(_state.recordsById, draft.id, draft);
    
    // Add to records array if not already present
    const existingIndex = _state.records.findIndex(d => d.id === draft.id);
    if (existingIndex === -1) {
      _state.records.push(draft);
    } else {
      Vue.set(_state.records, existingIndex, draft);
    }
  },

  UPDATE_DRAFT(_state, draft) {
    // Update in recordsById
    Vue.set(_state.recordsById, draft.id, draft);
    
    // Update in records array
    const index = _state.records.findIndex(d => d.id === draft.id);
    if (index !== -1) {
      Vue.set(_state.records, index, draft);
    }
  },

  REMOVE_DRAFT(_state, draftId) {
    // Remove from recordsById
    Vue.delete(_state.recordsById, draftId);
    
    // Remove from records array
    const index = _state.records.findIndex(d => d.id === draftId);
    if (index !== -1) {
      _state.records.splice(index, 1);
    }
    
    if (_state.selectedDraftId === draftId) {
      _state.selectedDraftId = null;
    }
  },

  SET_UI_FLAG(_state, { flag, value }) {
    _state.uiFlags[flag] = value;
  },

  SET_SELECTED_DRAFT(_state, draftId) {
    _state.selectedDraftId = draftId;
  },

  SET_STATUS_FILTER(_state, status) {
    _state.statusFilter = status;
  },

  SET_LABEL_FILTER(_state, label) {
    _state.labelFilter = label;
  },

  SET_CURRENT_CONVERSATION_ID(_state, conversationId) {
    _state.currentConversationId = conversationId;
  },

  CLEAR_DRAFTS(_state) {
    _state.records = [];
    _state.recordsById = {};
    _state.meta = {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      perPage: 15,
    };
    _state.selectedDraftId = null;
    _state.statusFilter = 'all';
    _state.labelFilter = 'all';
    _state.currentConversationId = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

